import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { FileSpreadsheet, Loader2, X, Upload, CheckCircle2, AlertTriangle, Undo2 } from 'lucide-react';

const BATCH = 5;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const FILE_STATUS = {
  pending: ['wartet', 'text-muted-foreground'], running: ['läuft', 'text-amber-300'], done: ['fertig', 'text-emerald-300'], failed: ['Fehler', 'text-red-400'],
};

function FileRows({ files }) {
  return (
    <div className="max-h-64 overflow-auto border border-border rounded-sm" data-testid="wb-file-rows">
      <table className="w-full text-[11px]">
        <thead className="bg-secondary/40 text-muted-foreground text-left"><tr><th className="px-2 py-1 font-medium">Workbook</th><th className="px-2 py-1 font-medium">Status</th><th className="px-2 py-1 font-medium text-right">Firmen</th><th className="px-2 py-1 font-medium text-right">Deals</th></tr></thead>
        <tbody className="divide-y divide-border/40">
          {files.map((f, i) => {
            const [label, cls] = FILE_STATUS[f.status] || FILE_STATUS.pending;
            return (
              <tr key={i} data-testid={`wb-file-row-${i}`}>
                <td className="px-2 py-1 truncate max-w-[320px]">{f.name}{f.error && <span className="block text-red-400 text-[10px]">{f.error}</span>}</td>
                <td className={`px-2 py-1 ${cls}`}>{f.status === 'running' ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{label}</span> : label}</td>
                <td className="px-2 py-1 text-right font-mono">{f.funds || 0}</td>
                <td className="px-2 py-1 text-right font-mono">{f.deals || 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function JobProgress({ job, onUndo, onReset }) {
  const [confirm, setConfirm] = useState(false);
  const pct = job.total_files ? Math.round((job.done_files / job.total_files) * 100) : 0;
  const running = job.status === 'running';
  return (
    <div className="space-y-2" data-testid="wb-job">
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        {running ? <Loader2 className="w-4 h-4 animate-spin text-amber-300" /> : job.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
        <span className="text-foreground font-medium" data-testid="wb-job-status">
          {running ? `Import läuft — Workbook ${Math.min(job.done_files + 1, job.total_files)} von ${job.total_files}` : job.status === 'done' ? `Import fertig — ${job.total_files} Workbooks` : job.status === 'undone' ? 'Import rückgängig gemacht' : `Import abgebrochen: ${job.error}`}
        </span>
        <span className="font-mono text-amber-300" data-testid="wb-job-counts">{job.funds_created} Firmen · {job.deals_saved} Deals</span>
        {running && job.current_file && <span className="text-muted-foreground truncate max-w-[280px]">{job.current_file}</span>}
        {job.failed_files > 0 && <span className="text-red-400">{job.failed_files} Datei(en) fehlgeschlagen</span>}
        <span className="text-muted-foreground/60 ml-auto">{fmtTime(job.created_at)}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} /></div>
      <FileRows files={job.files || []} />
      {!running && (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onReset} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="wb-job-new">weitere Workbooks importieren</button>
          {job.status === 'done' && job.funds_created > 0 && !confirm && (
            <button onClick={() => setConfirm(true)} className="text-[11px] text-red-400/80 hover:text-red-300 inline-flex items-center gap-1 ml-auto" data-testid="wb-job-undo">
              <Undo2 className="w-3 h-3" /> Import rückgängig ({job.funds_created} Firmen löschen)
            </button>
          )}
          {confirm && (
            <span className="inline-flex items-center gap-1.5 text-[11px] ml-auto" data-testid="wb-job-undo-confirm">
              Wirklich alle {job.funds_created} Firmen dieses Imports löschen?
              <button onClick={() => { setConfirm(false); onUndo(); }} className="px-2 h-6 rounded-sm bg-red-700 hover:bg-red-600 text-white" data-testid="wb-job-undo-yes">Ja, löschen</button>
              <button onClick={() => setConfirm(false)} className="px-2 h-6 rounded-sm border border-border" data-testid="wb-job-undo-no">Nein</button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function WorkbookImportPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const timer = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.wbJobs();
      const jobs = r.data.jobs || [];
      setHistory(jobs);
      const running = jobs.find(j => j.status === 'running');
      if (running) { setJob(running); setOpen(true); }
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!job || job.status !== 'running') { clearInterval(timer.current); return undefined; }
    timer.current = setInterval(async () => {
      try {
        const r = await api.wbJobGet(job.id);
        setJob(r.data);
        onChanged?.();
        if (r.data.status !== 'running') { clearInterval(timer.current); loadJobs(); toast.success(`Import fertig — ${r.data.funds_created} Firmen, ${r.data.deals_saved} Deals`); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(timer.current);
  }, [job?.id, job?.status, onChanged, loadJobs]);

  const addFiles = (list) => {
    const xl = [...list].filter(f => /\.xlsx?$|\.xlsm$/i.test(f.name));
    if (!xl.length) { toast.error('Bitte Excel-Workbooks (.xlsx) auswählen'); return; }
    setFiles(prev => [...prev, ...xl.filter(f => !prev.some(p => p.name === f.name && p.size === f.size))]);
  };

  const start = async () => {
    setUploading({ done: 0, total: files.length });
    try {
      const { data: { id } } = await api.wbJobCreate();
      for (let i = 0; i < files.length; i += BATCH) {
        const r = await api.wbJobFiles(id, files.slice(i, i + BATCH));
        if (r.data.rejected?.length) toast.warning(r.data.rejected.join(' · '));
        setUploading({ done: Math.min(i + BATCH, files.length), total: files.length });
      }
      await api.wbJobStart(id);
      const j = await api.wbJobGet(id);
      setJob(j.data);
      setFiles([]);
    } catch (e) { toast.error(e.response?.data?.detail || (e.response ? `Upload fehlgeschlagen (HTTP ${e.response.status})` : 'Upload fehlgeschlagen — Verbindung unterbrochen oder Timeout')); }
    setUploading(null);
  };

  const undo = async () => {
    try {
      const r = await api.wbJobUndo(job.id);
      toast.success(`${r.data.deleted_funds} Firmen gelöscht`);
      setJob(null); loadJobs(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rückgängig fehlgeschlagen'); }
  };

  const past = history.filter(j => j.id !== job?.id).slice(0, 4);
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="wb-import-panel">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid="wb-import-toggle">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300/80 inline-flex items-center gap-1.5"><FileSpreadsheet className="w-3 h-3" /> „Deals by Firm“-Workbooks importieren (Excel)</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Alle Workbooks auf einmal hochladen — jeder Tab wird eine neue Firma, alle Käufe und Verkäufe (inkl. Fonds-Vehikel, Buyer/Seller, Sub-Sektor, Strategie, Größe) werden als Deals gespeichert. Läuft im Hintergrund.
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">{job?.status === 'running' ? <span className="text-amber-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> läuft</span> : open ? 'zuklappen' : 'öffnen'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {job ? <JobProgress job={job} onUndo={undo} onReset={() => setJob(null)} /> : (
            <>
              <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }} onClick={() => !files.length && inputRef.current?.click()}
                data-testid="wb-dropzone"
                className={`border border-dashed rounded-sm p-4 transition-colors ${files.length ? 'border-border' : 'border-emerald-500/40 hover:border-emerald-400 cursor-pointer'}`}>
                {files.length ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="wb-files">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-secondary border border-border rounded-sm px-2 py-0.5" data-testid={`wb-file-${i}`}>
                        <FileSpreadsheet className="w-3 h-3 text-emerald-300" /> {f.name} <span className="text-muted-foreground/60">{(f.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground" data-testid={`wb-file-remove-${i}`}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3 inline-flex items-center gap-2 w-full justify-center">
                    <Upload className="w-4 h-4 text-emerald-300" /> Excel-Workbooks hierher ziehen oder klicken — alle 30 Dateien auf einmal möglich.
                  </p>
                )}
                <input ref={inputRef} type="file" accept=".xlsx,.xlsm" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} data-testid="wb-file-input" />
              </div>
              {files.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={start} disabled={!!uploading} data-testid="wb-start-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? `Hochladen ${uploading.done}/${uploading.total}…` : `${files.length} Workbook${files.length > 1 ? 's' : ''} importieren`}
                  </button>
                  <button onClick={() => inputRef.current?.click()} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="wb-add-more-btn">weitere hinzufügen</button>
                  <button onClick={() => setFiles([])} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="wb-clear-btn">leeren</button>
                  <span className="text-[10px] text-muted-foreground/70">Jeder Tab = neue Firma (auch wenn der Name schon existiert) · ~1 s pro Workbook</span>
                </div>
              )}
            </>
          )}
          {past.length > 0 && (
            <div className="space-y-1" data-testid="wb-history">
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Bisherige Importe</p>
              {past.map(j => (
                <button key={j.id} onClick={() => setJob(j)} className="w-full text-left text-[11px] flex items-center gap-2 flex-wrap hover:bg-secondary/30 rounded-sm px-2 py-1" data-testid={`wb-history-${j.id}`}>
                  <span className="text-muted-foreground/70 font-mono">{fmtTime(j.created_at)}</span>
                  <span className="text-foreground">{j.total_files} Workbooks</span>
                  <span className="text-amber-300 font-mono">{j.funds_created} Firmen · {j.deals_saved} Deals</span>
                  <span className={j.status === 'done' ? 'text-emerald-300' : j.status === 'undone' ? 'text-muted-foreground line-through' : 'text-red-400'}>{j.status === 'done' ? 'fertig' : j.status === 'undone' ? 'rückgängig' : 'Fehler'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
